/**
 * Dashboard module (#92): a neutral launcher — a greeting and a grid of
 * module cards rendered from the registry. It owns no document workflow.
 */

import template from './dashboard.template.html?raw';
import { renderModuleCard } from '../../../components/module-card';
import type { AppModule } from '../../shell/module.types';
import type { TranslationParams } from '../../../i18n/ui-manager.types';

export interface DashboardModuleDeps {
  getModules: () => readonly AppModule[];
  t: (key: string, params?: TranslationParams) => string;
  getUserName: () => string | null;
  onLanguageChange: (callback: () => void) => void;
}

export function createDashboardModule(deps: DashboardModuleDeps): AppModule {
  let root: HTMLElement | null = null;
  let visible = false;

  const render = (): void => {
    if (!root) return;

    const greeting = root.querySelector<HTMLElement>(
      '[data-dashboard-greeting]'
    );
    if (greeting) {
      const name = deps.getUserName();
      greeting.textContent = name
        ? deps.t('dashboard.greeting', { name })
        : deps.t('dashboard.greetingAnonymous');
    }

    const grid = root.querySelector<HTMLElement>('[data-dashboard-grid]');
    if (grid) {
      grid.innerHTML = deps
        .getModules()
        .filter((module) => module.showOnDashboard)
        .map((module) => renderModuleCard(module, (key) => deps.t(key)))
        .join('');
    }
  };

  return {
    id: 'dashboard',
    route: '/',
    labelKey: 'modules.dashboard.label',
    descriptionKey: 'modules.dashboard.description',
    icon: 'home',
    enabled: true,
    showOnDashboard: false,

    mount(outlet) {
      outlet.innerHTML = template;
      root = outlet;
      deps.onLanguageChange(() => {
        if (visible) render();
      });
    },

    onEnter() {
      visible = true;
      render();
    },

    onLeave() {
      visible = false;
    },
  };
}
